'use strict';

var FirebaseObject = require('./FirebaseObject');
var KayleeBase = require('./KayleeBase');
var KayleeCache = require('./KayleeCache');
var KayleeEvent = require('./KayleeEvent');
var KayleeEvents = require('./KayleeEvents');
var KayleeUtil = require('./KayleeUtil');
var KayleeUserMessages = require('./KayleeUserMessages');
var log = require('../log/Log').channel('KayleeUser');
var sutl = require('../sutl/Sutl');

var KayleeUser = {
    fromJson: fromJson,
    getUser: getUser,
    getUserByEmail: getUserByEmail,
    createUser: createUser,
};

var authRefreshTimeout = null;
var prevAuthUid = null;

KayleeBase.onAuthStateChanged(function (user) {
    var timestamp = Date.now();

    if (user === null) {
        // unauthentication event
        if (authRefreshTimeout !== null) {
            KayleeUtil.clearTimeout(authRefreshTimeout);
            authRefreshTimeout = null;
            prevAuthUid = null;
        }

        return;
    }

    // This handler gets registered on all environments (Inara, Mal, Jayne),
    // but we want different behavior for each. Jayne doesn't authenticate as
    // a user, so we shouldn't get here in that environment. To differentiate
    // between Mal and Inara, we look at the user IDs.

    var userType = KayleeBase.isInaraUserId(user.uid) ? 'inara' : 'mal';

    scheduleAuthTokenRefresh();

    if (userType === 'mal' && user.uid !== 'anonymousRemoteControl') {
        KayleeUser.getUser(user.uid)
            .$loaded()
            .then(function (user) {
                user.lastAuth = timestamp;

                if (prevAuthUid === null) {
                    user.lastLogin = timestamp;
                }

                prevAuthUid = user.uid;
                user.$save(false);
            });
    }
});

function fromJson(data) {
    var reqProps = ['$id'];
    return FirebaseObject.$fromJson(data, 'User', reqProps, reqProps, function (data) {
        return getUser(data.$id);
    });
}

// Returns a promise that resolves to the User object when it is loaded. If
// userId is not specified, then the userId of the current user is used.

function getUser(userId) {
    if (typeof userId === 'undefined' || userId === null) {
        if (KayleeBase.app.auth().currentUser === null) return null;
        userId = KayleeBase.app.auth().currentUser.uid;
    }

    var key = 'User: ' + userId;
    var user = KayleeCache.get(key);

    if (user === null) {
        user = new User(userId);
        KayleeCache.put(key, user);
    }

    return user;
}

function getUserByEmail(email) {
    return KayleeBase.app
        .database()
        .ref('users')
        .orderByChild('email')
        .equalTo(email)
        .once('value')
        .then(function (snap) {
            if (!snap.exists()) return Promise.reject(new Error('User not found.'));

            var users = snap.val();
            var userIds = Object.keys(users);

            if (userIds.length <= 0) return Promise.reject(new Error('User not found.'));
            if (userIds.length > 1) return Promise.reject(new Error('Duplicate emails.'));

            return getUser(userIds[0]);
        });
}

function addUpdatesToCreateUser(update, userId, firstName, lastName, email) {
    update['users/' + userId] = {
        created: Date.now(),
        email: email,
        firstName: firstName,
        lastName: lastName,
    };

    return KayleeEvent.addUpdatesForUserCreatedEvent(update, userId, firstName, lastName, email);
}

function createUser(firstName, lastName, email, password) {
    email = email.toLowerCase();

    var userString = '[User "' + firstName + '" "' + lastName + '" <' + email + '>]';

    log.debug('creating ' + userString);

    return KayleeBase.app
        .auth()
        .createUserWithEmailAndPassword(email, password)
        .then(function (fbUserCred) {
            userString =
                userString.slice(0, userString.length - 1) + ' (' + fbUserCred.user.uid + ')]';
            log.debug('created account for ' + userString + ', now adding user to database');
            return addUpdatesToCreateUser({}, fbUserCred.user.uid, firstName, lastName, email);
        })
        .then(function (update) {
            KayleeBase.update(update);
        })
        .then(
            function () {
                log.debug('created ' + userString);
            },
            function (err) {
                log.error('failed creating ' + userString + ', error: ' + err);
                return Promise.reject(err);
            }
        );
}

function scheduleAuthTokenRefresh(idTokenResult) {
    if (authRefreshTimeout !== null) {
        KayleeUtil.clearTimeout(authRefreshTimeout);
        authRefreshTimeout = null;
    }

    if (KayleeBase.app.auth().currentUser === null) return Promise.resolve();

    return (
        idTokenResult
            ? Promise.resolve(idTokenResult)
            : KayleeBase.app.auth().currentUser.getIdTokenResult()
    ).then(function (result) {
        // The Firebase SDK incorrectly declares claims.exp as a string. Be careful processing it in
        // case something changes or is bogus.

        const expirationTime = result.claims.exp;
        let timeoutMs = 10 * 60 * 1000; // use 10 minutes as a default if there's a problem

        if (typeof expirationTime === 'number') {
            timeoutMs = expirationTime * 1000 - 5 * 60 * 1000 - Date.now();
        } else {
            this.log.error(
                `unexpected expiration time: ${expirationTime} (${typeof expirationTime})`
            );
        }

        if (timeoutMs < 1000) {
            this.log.error(
                `expiration time suspiciously short: ${timeoutMs} ms, setting to 1000 ms`
            );
            timeoutMs = 1000;
        }

        log.info('next auth token refresh in ' + Math.ceil(timeoutMs / 1000) + ' seconds');

        KayleeUtil.setTimeout(function () {
            log.info('refreshing auth token');
            authRefreshTimeout = null;
            KayleeBase.app.auth().currentUser.getIdTokenResult(true).then(scheduleAuthTokenRefresh);
            // TODO: write to one of the following:
            //     user.lastAuthRefresh
            //     controller.lastAuthRefresh
        }, timeoutMs);
    });
}

function User(userId) {
    this.$nodeType = 'user';
    FirebaseObject.call(this, KayleeBase.app.database().ref('users/' + userId));
}

User.prototype = Object.create(FirebaseObject.prototype);
User.prototype.constructor = User;

User.prototype.$defaults = {
    privileges: {
        developer: false,
        sales: false,
        support: false,
    },
};

User.prototype.$includeInChangedEvent = {
    email: true,
    firstName: true,
    lastAuth: true,
    lastLogin: true,
    lastName: true,
    mobileNumber: true,
};

User.prototype.$cantSave = {
    email: true,
};

User.prototype.$addUpdatesForChangedEvent = KayleeEvent.addUpdatesForUserChangedEvent;

User.prototype.toString = function () {
    if (this.$internalIsLoaded) {
        return (
            '[User "' +
            this.firstName +
            '" "' +
            this.lastName +
            '" <' +
            this.email +
            '> (' +
            this.$id +
            ')]'
        );
    }

    return '[User (' + this.$id + ')]';
};

User.prototype.$getRole = function (node) {
    if (typeof node === 'undefined' || node === null) {
        return KayleeBase.isDbRoleEnabled && this.$getRoleForDb();
    } else if (node.$nodeType === 'company') {
        return this.$getRoleForCompany(node);
    } else if (node.$nodeType === 'site') {
        return this.$getRoleForSite(node);
    } else if (node.$nodeType === 'room') {
        return this.$getRoleForRoom(node);
    }

    log.error('invalid node type ' + node.nodeType);
    return null;
};

User.prototype.$getRoleForDb = function () {
    if (!KayleeBase.isDbRoleEnabled) return null;
    return sutl.getNode(this, ['roles', 'role']);
};

User.prototype.$getRoleForCompany = function (companyOrId) {
    if (typeof companyOrId === 'undefined' || companyOrId === null) return 'zero';

    var companyId = companyOrId.hasOwnProperty('$id') ? companyOrId.$id : companyOrId;

    var dbRole =
        (KayleeBase.isDbRoleEnabled ? sutl.getNode(this, ['roles', 'role']) : null) || 'zero';
    var companyRole = sutl.getNode(this, ['roles', 'companies', companyId, 'role']) || 'zero';
    var childrenRole = sutl.nodeExists(this, ['roles', 'companies', companyId]) ? 'viewer' : 'zero';
    var bestRole = dbRole;

    if (companyRole < bestRole) bestRole = companyRole;
    if (childrenRole < bestRole) bestRole = childrenRole;
    if (bestRole === 'zero') bestRole = null;

    return bestRole;
};

User.prototype.$getRoleForSite = function (site) {
    if (typeof site === 'undefined' || site === null) return 'zero';

    var dbRole =
        (KayleeBase.isDbRoleEnabled ? sutl.getNode(this, ['roles', 'role']) : null) || 'zero';
    var companyRole = sutl.getNode(this, ['roles', 'companies', site.companyId, 'role']) || 'zero';
    var siteRole =
        sutl.getNode(this, ['roles', 'companies', site.companyId, 'sites', site.$id, 'role']) ||
        'zero';
    var childrenRole = sutl.nodeExists(this, [
        'roles',
        'companies',
        site.companyId,
        'sites',
        site.$id,
    ])
        ? 'viewer'
        : 'zero';
    var bestRole = dbRole;

    if (companyRole < bestRole) bestRole = companyRole;
    if (siteRole < bestRole) bestRole = siteRole;
    if (childrenRole < bestRole) bestRole = childrenRole;
    if (bestRole === 'zero') bestRole = null;

    return bestRole;
};

User.prototype.$getRoleForRoom = function (room) {
    if (typeof room === 'undefined' || room === null) return 'zero';

    var dbRole =
        (KayleeBase.isDbRoleEnabled ? sutl.getNode(this, ['roles', 'role']) : null) || 'zero';
    var companyRole = sutl.getNode(this, ['roles', 'companies', room.companyId, 'role']) || 'zero';
    var siteRole =
        sutl.getNode(this, ['roles', 'companies', room.companyId, 'sites', room.siteId, 'role']) ||
        'zero';
    var roomRole =
        sutl.getNode(this, [
            'roles',
            'companies',
            room.companyId,
            'sites',
            room.siteId,
            'rooms',
            room.$id,
            'role',
        ]) || 'zero';
    var bestRole = dbRole;

    if (companyRole < bestRole) bestRole = companyRole;
    if (siteRole < bestRole) bestRole = siteRole;
    if (roomRole < bestRole) bestRole = roomRole;
    if (bestRole === 'zero') bestRole = null;

    return bestRole;
};

// Returns a boolean indicating whether or not the user can watch all companies
// in the database. The decision is made based on the roles data for the user,
// but the database role can be overridden with the newDbRole argument.

User.prototype.$canWatchAllCompanies = function (newDbRole) {
    var dbRole = newDbRole;

    if (typeof dbRole === 'undefined') {
        dbRole = KayleeBase.isDbRoleEnabled ? sutl.getNode(this.roles, ['role']) : null;
    }

    if (dbRole === null) return false;
    return true;
};

// Returns a boolean indicating whether or not the user can watch all sites for
// the specified company. The decision is made based on the roles data for the
// user, but the database role or company role can be overridden with additional
// arguments.

User.prototype.$canWatchAllSitesForCompany = function (companyId, newDbRole, newCompanyRole) {
    if (this.$canWatchAllCompanies(newDbRole)) {
        return true;
    }

    var companyRole = newCompanyRole;

    if (typeof companyRole === 'undefined') {
        companyRole = sutl.getNode(this.roles, ['companies', companyId, 'role']);
    }

    if (companyRole === null) return false;
    return true;
};

// Returns a boolean indicating whether or not the user can watc all rooms for
// the specified site. The decision is made based on the roles data for the
// user, but the database role, company role, or site role can be overridden
// with additional arguments.

User.prototype.$canWatchAllRoomsForSite = function (
    companyId,
    siteId,
    newDbRole,
    newCompanyRole,
    newSiteRole
) {
    if (this.$canWatchAllSitesForCompany(companyId, newDbRole, newCompanyRole)) {
        return true;
    }

    var siteRole = newSiteRole;

    if (typeof siteRole === 'undefined') {
        siteRole = sutl.getNode(this.roles, ['companies', companyId, 'sites', siteId, 'role']);
    }

    if (siteRole === null) return false;
    return true;
};

User.prototype.$addUpdatesToSetDbRole = function (update, role, actorUserId) {
    if (typeof role === 'undefined') role = null;
    if (typeof actorUserId === 'undefined' || actorUserId === null) {
        if (KayleeBase.app.auth().currentUser) {
            actorUserId = KayleeBase.app.auth().currentUser.uid;
        }
    }

    if (!this.roles.hasOwnProperty(role) && role !== null) {
        update['users/' + this.$id + '/roles/role'] = role;
        return KayleeEvent.addUpdatesForRoleCreatedEvent(
            update,
            this.$id,
            role,
            null,
            null,
            null,
            actorUserId
        );
    } else if (this.roles.hasOwnProperty(role) && role !== null && this.roles.role !== role) {
        update['users/' + this.$id + '/roles/role'] = role;
        return KayleeEvent.addUpdatesForRoleChangedEvent(
            update,
            this.$id,
            this.roles.role,
            role,
            null,
            null,
            null,
            actorUserId
        );
    } else if (this.roles.hasOwnProperty(role) && role === null) {
        update['users/' + this.$id + '/roles/role'] = null;
        return KayleeEvent.addUpdatesForRoleDeletedEvent(
            update,
            this.$id,
            this.roles.role,
            null,
            null,
            null,
            actorUserId
        );
    }

    return Promise.resolve(update);
};

User.prototype.$addUpdatesToSetCompanyRole = function (update, company, role, actorUserId) {
    var self = this;

    if (typeof role === 'undefined') role = null;
    if (typeof actorUserId === 'undefined' || actorUserId === null) {
        if (KayleeBase.app.auth().currentUser) {
            actorUserId = KayleeBase.app.auth().currentUser.uid;
        }
    }

    var hasRoleForCompany = sutl.nodeExists(self.roles, ['companies', company.$id, 'role']);
    var rolePath = 'users/' + self.$id + '/roles/companies/' + company.$id + '/role';
    var currRole;

    if (hasRoleForCompany) {
        currRole = self.roles.companies[company.$id].role;
    }

    if (!hasRoleForCompany && role !== null) {
        update[rolePath] = role;

        // We're adding a role for this company for which the user did not
        // previously have a role. We need to make sure the user is listed in
        // the indices for the company, all if its sites, and all of its rooms.

        update['index/usersByCompany/' + company.$id + '/' + self.$id] = true;

        return KayleeUtil.loadSitesByCompanyIndex({ companyId: company.$id }, false)
            .then(function (obj) {
                var roomIndexPromises = [];

                for (var siteId in obj.sitesByCompany) {
                    if (!obj.sitesByCompany.hasOwnProperty(siteId)) continue;
                    update['index/usersBySite/' + siteId + '/' + self.$id] = true;
                    roomIndexPromises.push(
                        KayleeUtil.loadRoomsBySiteIndex({ siteId: siteId }, false)
                    );
                }

                return Promise.all(roomIndexPromises);
            })
            .then(function (roomIndices) {
                for (var i = 0; i < roomIndices.length; i++) {
                    var roomsBySite = roomIndices[i].roomsBySite;

                    for (var roomId in roomsBySite) {
                        if (!roomsBySite.hasOwnProperty(roomId)) continue;
                        update['index/usersByRoom/' + roomId + '/' + self.$id] = true;
                    }
                }
            })
            .then(function () {
                return KayleeEvent.addUpdatesForRoleCreatedEvent(
                    update,
                    self.$id,
                    role,
                    company.$id,
                    null,
                    null,
                    actorUserId
                );
            });
    } else if (hasRoleForCompany && role !== null && currRole !== role) {
        update[rolePath] = role;

        // The value of the role changed, but a role already existed, so we
        // don't need to update any indices.

        return KayleeEvent.addUpdatesForRoleChangedEvent(
            update,
            self.$id,
            currRole,
            role,
            company.$id,
            null,
            null,
            actorUserId
        );
    } else if (hasRoleForCompany && role === null) {
        update[rolePath] = null;

        // We're removing the previously existing role for this company. We need
        // to figure out if we need to remove the user from the usersByCompany,
        // usersBySite, and usersByRoom indices.
        //
        // usersByCompany/company.$id: The user will be present in this index if
        // he has a role for any of the company's sites or rooms.
        //
        // usersBySite/<siteIds for sites belonging to this company>: The user
        // will be present in this index if he has a role for that site or any
        // of its rooms.
        //
        // usersByRoom/<roomIds for rooms belonging to this company>: The user
        // will be present in this index if he has a role for that room or its
        // site.

        var siteId, roomId;

        if (!sutl.nodeExists(self.roles, ['companies', company.$id, 'sites'])) {
            update['index/usersByCompany/' + company.$id + '/' + self.$id] = null;
        }

        return KayleeUtil.loadSitesByCompanyIndex({ companyId: company.$id }, false)
            .then(function (obj) {
                var roomPromises = [];
                var removeRoomRoles = function (obj) {
                    for (roomId in obj.roomsBySite) {
                        if (!obj.roomsBySite.hasOwnProperty(roomId)) continue;
                        if (
                            !sutl.nodeExists(self.roles, [
                                'companies',
                                company.$id,
                                'sites',
                                obj.siteId,
                                'rooms',
                                roomId,
                                'role',
                            ])
                        ) {
                            update['index/usersByRoom/' + roomId + '/' + self.$id] = null;
                        }
                    }
                };

                for (siteId in obj.sitesByCompany) {
                    if (!obj.sitesByCompany.hasOwnProperty(siteId)) continue;
                    if (!sutl.nodeExists(self.roles, ['companies', company.$id, 'sites', siteId])) {
                        update['index/usersBySite/' + siteId + '/' + self.$id] = null;
                    }

                    if (
                        !sutl.nodeExists(self.roles, [
                            'companies',
                            company.$id,
                            'sites',
                            siteId,
                            'role',
                        ])
                    ) {
                        roomPromises.push(
                            KayleeUtil.loadRoomsBySiteIndex({ siteId: siteId }, false).then(
                                removeRoomRoles
                            )
                        );
                    }
                }

                return Promise.all(roomPromises);
            })
            .then(function () {
                return KayleeEvent.addUpdatesForRoleDeletedEvent(
                    update,
                    self.$id,
                    currRole,
                    company.$id,
                    null,
                    null,
                    actorUserId
                );
            });
    }

    return Promise.resolve(update);
};

User.prototype.$addUpdatesToSetSiteRole = function (update, site, role, actorUserId) {
    var self = this;

    if (typeof role === 'undefined') role = null;
    if (typeof actorUserId === 'undefined' || actorUserId === null) {
        if (KayleeBase.app.auth().currentUser) {
            actorUserId = KayleeBase.app.auth().currentUser.uid;
        }
    }

    var hasRoleForSite = sutl.nodeExists(self.roles, [
        'companies',
        site.companyId,
        'sites',
        site.$id,
        'role',
    ]);
    var rolePath =
        'users/' + self.$id + '/roles/companies/' + site.companyId + '/sites/' + site.$id + '/role';
    var currRole;

    if (hasRoleForSite) {
        currRole = self.roles.companies[site.companyId].sites[site.$id].role;
    }

    if (!hasRoleForSite && role !== null) {
        update[rolePath] = role;

        // We're adding a role for this site for which the user did not
        // previously have a role. We need to make sure the user is listed in
        // the indices for the company, the site, and all the site's rooms.

        update['index/usersByCompany/' + site.companyId + '/' + self.$id] = true;
        update['index/usersBySite/' + site.$id + '/' + self.$id] = true;

        return KayleeUtil.loadRoomsBySiteIndex({ siteId: site.$id }, false)
            .then(function (obj) {
                for (var roomId in obj.roomsBySite) {
                    if (!obj.roomsBySite.hasOwnProperty(roomId)) continue;
                    update['index/usersByRoom/' + roomId + '/' + self.$id] = true;
                }
            })
            .then(function () {
                return KayleeEvent.addUpdatesForRoleCreatedEvent(
                    update,
                    self.$id,
                    role,
                    site.companyId,
                    site.$id,
                    null,
                    actorUserId
                );
            });
    } else if (hasRoleForSite && role !== null && currRole !== role) {
        update[rolePath] = role;

        // The value of the role changed, but a role already existed, so we
        // don't need to update any indices.

        return KayleeEvent.addUpdatesForRoleChangedEvent(
            update,
            self.$id,
            currRole,
            role,
            site.companyId,
            site.$id,
            null,
            actorUserId
        );
    } else if (hasRoleForSite && role === null) {
        // Remove the site role for this user.
        update[rolePath] = null;

        // We're removing the previously existing role for this site. We need to
        // figure out if we need to remove the user from the usersByCompany,
        // usersBySite, and usersByRoom indices.
        //
        // usersByCompany/site.companyId: The user will be present in this index
        // if he still has a role for the company, any of its other sites, or
        // any of its rooms.
        //
        // usersBySites/site.$id: The user will be present in this index if he
        // has a role for the company or any of the site's rooms.
        //
        // usersByRoom/<roomIds for rooms belonging to this site>: The user will
        // be present in this index if he has a role for the company or the
        // room.

        var hasRoleForCompany = sutl.nodeExists(self.roles, ['companies', site.companyId, 'role']);
        var hasRoleForOtherSites = false;
        var hasRoleForThisCompanysRooms = false;
        var hasRoleForThisSitesRooms = false;
        var sites = sutl.getNode(self.roles, ['companies', site.companyId, 'sites']);
        var siteId, roomId;

        for (siteId in sites) {
            if (!sites.hasOwnProperty(siteId)) continue;

            if (siteId !== site.$id && sites[siteId].hasOwnProperty('role')) {
                hasRoleForOtherSites = true;
            }

            var rooms = sutl.getNode(sites[siteId], ['rooms']);

            if (rooms !== null) {
                hasRoleForThisCompanysRooms = true;

                if (siteId === site.$id) {
                    hasRoleForThisSitesRooms = true;
                }
            }
        }

        if (!hasRoleForCompany && !hasRoleForOtherSites && !hasRoleForThisCompanysRooms) {
            update['index/usersByCompany/' + site.companyId + '/' + self.$id] = null;
        }

        if (!hasRoleForCompany && !hasRoleForThisSitesRooms) {
            update['index/usersBySite/' + site.$id + '/' + self.$id] = null;
        }

        return KayleeUtil.loadRoomsBySiteIndex({ siteId: site.$id }, false)
            .then(function (obj) {
                var roomRoles = sutl.getNode(self, [
                    'roles',
                    'companies',
                    site.companyId,
                    'sites',
                    site.$id,
                    'rooms',
                ]);

                if (hasRoleForCompany) return;

                for (roomId in obj.roomsBySite) {
                    if (!obj.roomsBySite.hasOwnProperty(roomId)) continue;
                    if (!sutl.nodeExists(roomRoles, [roomId, 'role'])) {
                        update['index/usersByRoom/' + roomId + '/' + self.$id] = null;
                    }
                }
            })
            .then(function () {
                return KayleeEvent.addUpdatesForRoleDeletedEvent(
                    update,
                    self.$id,
                    currRole,
                    site.companyId,
                    site.$id,
                    null,
                    actorUserId
                );
            });
    }

    return Promise.resolve(update);
};

User.prototype.$addUpdatesToSetRoomRole = function (update, room, role, actorUserId) {
    var self = this;

    if (typeof role === 'undefined') role = null;
    if (typeof actorUserId === 'undefined' || actorUserId === null) {
        if (KayleeBase.app.auth().currentUser) {
            actorUserId = KayleeBase.app.auth().currentUser.uid;
        }
    }

    var hasRoleForRoom = sutl.nodeExists(self.roles, [
        'companies',
        room.companyId,
        'sites',
        room.siteId,
        'rooms',
        room.$id,
        'role',
    ]);
    var rolePath =
        'users/' +
        self.$id +
        '/roles/companies/' +
        room.companyId +
        '/sites/' +
        room.siteId +
        '/rooms/' +
        room.$id +
        '/role';
    var currRole;

    if (hasRoleForRoom) {
        currRole = self.roles.companies[room.companyId].sites[room.siteId].rooms[room.$id].role;
    }

    if (!hasRoleForRoom && role !== null) {
        update[rolePath] = role;

        // We're adding a role for this room for which the user did not
        // previously have a role. We need to make surethe user is listed in the
        // indices for the company, the site, and the room.

        update['index/usersByRoom/' + room.$id + '/' + self.$id] = true;
        update['index/usersBySite/' + room.siteId + '/' + self.$id] = true;
        update['index/usersByCompany/' + room.companyId + '/' + self.$id] = true;
        return KayleeEvent.addUpdatesForRoleCreatedEvent(
            update,
            self.$id,
            role,
            room.companyId,
            room.siteId,
            room.$id,
            actorUserId
        );
    } else if (hasRoleForRoom && role !== null && currRole !== role) {
        update[rolePath] = role;

        // The value of the role changed, but a role already existed, so we
        // don't need to update any indices.

        return KayleeEvent.addUpdatesForRoleChangedEvent(
            update,
            self.$id,
            currRole,
            role,
            room.companyId,
            room.siteId,
            room.$id,
            actorUserId
        );
    } else if (hasRoleForRoom && role === null) {
        update[rolePath] = null;

        // We're removing the previously existing role for this room. We need to
        // figure out if we need to remove the user from the usersByCompany,
        // usersBySite, and usersByRoom indices.
        //
        // usersByCompany/room.companyId: The user will be present in this index
        // if he has a role for the company, any of its sites, or any of its
        // other rooms.
        //
        // usersBySite/room.siteId: The user will be present in this index if he
        // has a role for the company, the site, or any of the sites other
        // rooms.
        //
        // usersByRoom/room.$id: The user will be present in this index if he
        // has a role for the company or the site.

        var hasRoleForCompany = sutl.nodeExists(self.roles, ['companies', room.companyId, 'role']);
        var hasRoleForCompanysSites = false;
        var hasRoleForCompanysOtherRooms = false;
        var hasRoleForSite = sutl.nodeExists(self.roles, [
            'companies',
            room.companyId,
            'sites',
            room.siteId,
            'role',
        ]);
        var hasRoleForSitesOtherRooms = false;
        var sites = sutl.getNode(self.roles, ['companies', room.companyId, 'sites']);

        for (var siteId in sites) {
            if (!sites.hasOwnProperty(siteId)) continue;

            if (sites[siteId].hasOwnProperty('role')) {
                hasRoleForCompanysSites = true;
            }

            var rooms = sutl.getNode(sites[siteId], ['rooms']);

            for (var roomId in rooms) {
                if (!rooms.hasOwnProperty(roomId)) continue;

                if (rooms[roomId].hasOwnProperty('role')) {
                    if (roomId !== room.$id) {
                        hasRoleForCompanysOtherRooms = true;

                        if (siteId === room.siteId) {
                            hasRoleForSitesOtherRooms = true;
                        }
                    }
                }

                if (roomId !== room.$id && rooms[roomId].hasOwnProperty('role')) {
                    hasRoleForCompanysOtherRooms = true;
                }
            }
        }

        if (!hasRoleForCompany && !hasRoleForCompanysSites && !hasRoleForCompanysOtherRooms) {
            update['index/usersByCompany/' + room.companyId + '/' + self.$id] = null;
        }

        if (!hasRoleForCompany && !hasRoleForSite && !hasRoleForSitesOtherRooms) {
            update['index/usersBySite/' + room.siteId + '/' + self.$id] = null;
        }

        if (!hasRoleForCompany && !hasRoleForSite) {
            update['index/usersByRoom/' + room.$id + '/' + self.$id] = null;
        }

        return KayleeEvent.addUpdatesForRoleDeletedEvent(
            update,
            self.$id,
            currRole,
            room.companyId,
            room.siteId,
            room.$id,
            actorUserId
        );
    }

    return Promise.resolve(update);
};

// Set the user's role on the specified node. Returns a promise that resolves
// when the operation is complete.

User.prototype.$setRole = function (node, role) {
    if (!this.$internalIsLoaded) {
        var err = new Error(this + ' not loaded');
        log.error(err.message);
        return Promise.reject(err);
    }

    var self = this;

    if (typeof role === 'undefined') role = null;

    var updatePromise;

    if (node.$nodeType === 'company') {
        updatePromise = self.$addUpdatesToSetCompanyRole({}, node, role);
    } else if (node.$nodeType === 'site') {
        updatePromise = self.$addUpdatesToSetSiteRole({}, node, role);
    } else if (node.$nodeType === 'room') {
        updatePromise = self.$addUpdatesToSetRoomRole({}, node, role);
    } else {
        return Promise.reject(new Error('invalid node'));
    }

    return updatePromise
        .then(function (update) {
            return KayleeBase.update(update);
        })
        .then(function () {
            var nodeName = '$loaded' in node ? node.toString() : node.$type + ' ' + node.$id;
            log.debug('set role for ' + self + ' on ' + nodeName + ' to ' + role);
        });
};

User.prototype.$toJson = function () {
    return FirebaseObject.prototype.$toJson.call(this, ['$id'], {});
};

User.prototype.$updateEmail = function (email) {
    if (!this.$internalIsLoaded) {
        var err = new Error(this + ' not loaded');
        log.error(err.message);
        return Promise.reject(err);
    }

    if (this.email === email) {
        log.debug("nothing to do, email didn't change");
        return Promise.resolve();
    }

    var self = this;

    return KayleeBase.app
        .auth()
        .currentUser.updateEmail(email)
        .then(function () {
            var update = {};
            update['users/' + self.$id + '/email'] = email;
            return KayleeEvent.addUpdatesForUserChangedEvent(update, self.$id, self, {
                email: email,
            });
        })
        .then(function (update) {
            return KayleeBase.update(update);
        });
};

User.prototype.$updatePassword = function (password) {
    if (!this.$internalIsLoaded) {
        var err = new Error(this + ' not loaded');
        log.error(err.message);
        return Promise.reject(err);
    }

    var self = this;
    return KayleeBase.app
        .auth()
        .currentUser.updatePassword(password)
        .then(function () {
            return KayleeEvent.addUpdatesForUserPasswordChangedEvent({}, self.$id);
        })
        .then(function (update) {
            return KayleeBase.update(update);
        });
};

User.prototype.$getEvents = function (limit) {
    return KayleeEvents.getEvents(this, limit);
};

User.prototype.$getMessages = function () {
    return KayleeUserMessages.getUserMessages(this.$id);
};

User.prototype.$requestBuild = function (branch) {
    if (KayleeBase.alias === 'persephone') {
        return KayleeBase.request('userBuildRequest', { branch: branch });
    }
    return Promise.reject('not permitted');
};

module.exports = KayleeUser;
